import {
  addYears,
  format,
  getMonth,
  getYear,
  isToday,
  isYesterday,
  parseISO,
  subDays,
} from "date-fns";

import { formatDistanceToNow } from "date-fns";
import { queryClient } from "utils/react-query";

export function filterNavItemsByPermission(navItems, permissions) {
  if (!navItems) return [];

  return navItems
    .map((item) => {
      // Check if this item has a permission requirement
      if (item.requiredPermission && !permissions[item.requiredPermission]) {
        return null; // Filter out items user doesn't have permission for
      }

      // Also check if submenu items have permissions
      const filteredItems = item.items
        ? item.items.filter(
            (subItem) =>
              !subItem.requiredPermission ||
              permissions[subItem.requiredPermission],
          )
        : [];

      // Return item with filtered subitems
      return {
        ...item,
        items: filteredItems.length > 0 ? filteredItems : undefined,
      };
    })
    .filter(Boolean); // Remove null items
}

/**
 * Calculates total hours from entries
 * @param {Array} entries - Array of timesheet entries
 * @param {string} field - Field to sum (logged_hours or approved_hours)
 * @returns {number} Total hours
 */
export const calculateTotalHours = (entries, field) => {
  return entries.reduce((sum, entry) => sum + (Number(entry[field]) || 0), 0);
};

export function formatDateForAPI(date) {
  return date ? format(date, "yyyy-MM-dd") : "";
}

/**
 * Formats a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export function formatDateForDisplay(dateString) {
  if (!dateString) return "-";
  const date =
    typeof dateString === "string" ? parseISO(dateString) : dateString;
  return format(date, "dd MMM, yyyy");
}

export function formatDateTimeForDisplay(dateString) {
  if (!dateString) return "-";
  const date =
    typeof dateString === "string" ? parseISO(dateString) : dateString;
  return format(date, "dd MMM yyyy, hh:mm a");
}

/**
 * Gets the last N days including today
 * @param {number} numberOfDays - Number of days to get
 * @returns {string[]} Array of dates in ISO format
 */
export const getLastNDays = (numberOfDays) => {
  const today = new Date();
  return Array.from({ length: numberOfDays }, (_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    return date.toISOString().split("T")[0];
  });
};

/**
 * Gets descriptive day name from date
 * @param {string} dateString - ISO date string
 * @returns {string} Day name with context (Today, Yesterday, or weekday)
 */
export function getDayFromDate(dateString) {
  const date =
    typeof dateString === "string" ? parseISO(dateString) : dateString;

  const day = format(date, "EEEE");
  if (isToday(date)) {
    return day + " (Today)";
  } else if (isYesterday(date)) {
    return day + " (Yesterday)";
  } else {
    return day;
  }
}

export function maskPrivacyAmount(amount) {
  if (amount == null) return "";
  const str = String(amount);
  const [intPart] = str.split(".");
  const digits = intPart.replace(/[^0-9]/g, "");
  // Braille Unicode range: 0x2800 - 0x28FF
  let result = "";
  for (let i = 0; i < digits.length; i++) {
    // Pick a random Braille character
    const code = 0x2800 + Math.floor(Math.random() * (0x28ff - 0x2800 + 1));
    result += String.fromCharCode(code);
  }
  return result;
}

/**
 * Formats a date as a relative time string (e.g., "5 days ago").
 * @param {string|Date} date - The date to format.
 * @param {object} options - Options for formatting (e.g., { addSuffix: true }).
 * @returns {string}
 */
export function formatRelativeDate(date, options = { addSuffix: true }) {
  if (!date) return "";
  return formatDistanceToNow(new Date(date), options);
}

export function formatAmount(value) {
  if (value >= 10000000) {
    // Crores
    return (
      (value / 10000000).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      }) + "Cr"
    );
  } else if (value >= 100000) {
    // Lakhs
    return (
      (value / 100000).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      }) + "L"
    );
  } else {
    // Thousands & below
    return value.toLocaleString("en-IN");
  }
}

export async function invalidateAllQueries({ refetch = true } = {}) {
  // Mark all queries as stale so any active observers will refetch on next render/focus
  await queryClient.invalidateQueries({ predicate: () => true });
  // Optionally refetch all currently active queries immediately
  if (refetch) {
    await queryClient.refetchQueries({ predicate: (q) => q.isActive() });
  }
}

/**
 * Replaces underscores with spaces and capitalizes the first letter of each word.
 * @param {string} str - The string to format.
 * @returns {string} The formatted string.
 */
export function formatLabel(str) {
  if (!str) return "";
  return str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getCurrentFinancialYearRange() {
  const today = new Date();
  const month = getMonth(today); // 0-based; 3 = April
  const year = getYear(today);
  const fyStartDate =
    month >= 3 ? new Date(year, 3, 1) : new Date(year - 1, 3, 1);
  const fyEndDate = subDays(addYears(fyStartDate, 1), 1);
  return {
    start: formatDateForAPI(fyStartDate),
    end: formatDateForAPI(fyEndDate),
  };
}

const { start, end } = getCurrentFinancialYearRange();
export const FY_START_DATE = start;
export const FY_END_DATE = end;

export function getCurrentFY() {
  const fyStartYear = new Date(FY_START_DATE).getFullYear();
  return `FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`;
}
